import React, { useState } from 'react';
import ExercisePicker from './ExercisePicker';
import { PLAN_GOALS, newPlanExercise, takesLoad } from './strengthFormat';

const toNumber = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

function NumberField({ label, value, onChange, step = 1, min = 0, max, width = '4.5rem' }) {
  return (
    <label className="st-mini-field">
      <span>{label}</span>
      <input
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={value ?? ''}
        style={{ width }}
        onChange={(e) => onChange(toNumber(e.target.value))}
      />
    </label>
  );
}

function PlanEditor({ t, lang, initial, isNew, catalog, catalogByKey, saving, onSave, onCancel }) {
  const [plan, setPlan] = useState(() => JSON.parse(JSON.stringify(initial)));
  // { dayIndex, exerciseIndex } — exerciseIndex null means "add", otherwise "replace"
  const [picker, setPicker] = useState(null);
  const [error, setError] = useState(null);

  const update = (field, value) => setPlan(p => ({ ...p, [field]: value }));
  const updateDay = (di, field, value) =>
    setPlan(p => ({ ...p, days: p.days.map((d, i) => (i === di ? { ...d, [field]: value } : d)) }));
  const updateExercise = (di, ei, changes) =>
    setPlan(p => ({
      ...p,
      days: p.days.map((d, i) => (i !== di ? d : { ...d, exercises: d.exercises.map((e, j) => (j === ei ? { ...e, ...changes } : e)) }))
    }));
  const removeExercise = (di, ei) =>
    setPlan(p => ({ ...p, days: p.days.map((d, i) => (i !== di ? d : { ...d, exercises: d.exercises.filter((_, j) => j !== ei) })) }));
  const moveExercise = (di, ei, direction) =>
    setPlan(p => ({
      ...p,
      days: p.days.map((d, i) => {
        if (i !== di) return d;
        const target = ei + direction;
        if (target < 0 || target >= d.exercises.length) return d;
        const list = [...d.exercises];
        [list[ei], list[target]] = [list[target], list[ei]];
        return { ...d, exercises: list };
      })
    }));

  const pick = (key) => {
    const exercise = catalogByKey[key];
    const { dayIndex, exerciseIndex } = picker;
    if (exerciseIndex === null) {
      setPlan(p => ({
        ...p,
        days: p.days.map((d, i) => (i === dayIndex ? { ...d, exercises: [...d.exercises, newPlanExercise(exercise)] } : d))
      }));
    } else {
      // Swapping keeps sets and rest, but the target type follows the new exercise.
      const fresh = newPlanExercise(exercise);
      const current = plan.days[dayIndex].exercises[exerciseIndex];
      const sameKind = catalogByKey[current.exerciseKey]?.kind === exercise.kind;
      updateExercise(dayIndex, exerciseIndex, sameKind
        ? { exerciseKey: key, targetWeightKg: null }
        : { ...fresh, targetSets: current.targetSets, restSec: current.restSec });
    }
    setPicker(null);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!plan.name.trim()) { setError(t('strength.editor.nameRequired')); return; }
    if (plan.days.every(d => d.exercises.length === 0)) { setError(t('strength.editor.exerciseRequired')); return; }
    if (plan.days.some(d => !d.name.trim())) { setError(t('strength.editor.dayNameRequired')); return; }
    setError(null);
    onSave({ ...plan, days: plan.days.filter(d => d.exercises.length > 0) });
  };

  return (
    <form className="st-card st-editor" onSubmit={submit}>
      <h2>{isNew ? t('strength.editor.newTitle') : t('strength.editor.editTitle')}</h2>

      <div className="st-form-grid">
        <label className="st-field st-field-wide">
          <span>{t('strength.editor.name')}</span>
          <input className="st-input" value={plan.name} maxLength={200} onChange={(e) => update('name', e.target.value)} required />
        </label>
        <label className="st-field">
          <span>{t('strength.editor.goal')}</span>
          <select className="st-select" value={plan.goal} onChange={(e) => update('goal', e.target.value)}>
            {PLAN_GOALS.map(g => <option key={g} value={g}>{t(`strength.goals.${g}`)}</option>)}
          </select>
        </label>
        <label className="st-field">
          <span>{t('strength.editor.daysPerWeek')}</span>
          <select className="st-select" value={plan.daysPerWeek} onChange={(e) => update('daysPerWeek', Number(e.target.value))}>
            {[1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="st-field st-field-wide">
          <span>{t('strength.editor.description')}</span>
          <textarea className="st-input" rows={2} value={plan.description || ''} maxLength={2000} onChange={(e) => update('description', e.target.value)} />
        </label>
      </div>

      {plan.days.map((day, di) => (
        <fieldset key={di} className="st-editor-day">
          <legend className="st-sr-only">{day.name}</legend>
          <div className="st-row st-row-between">
            <input
              className="st-input st-day-name"
              value={day.name}
              maxLength={100}
              aria-label={t('strength.editor.dayLabel')}
              onChange={(e) => updateDay(di, 'name', e.target.value)}
            />
            {plan.days.length > 1 && (
              <button
                type="button"
                className="st-btn st-btn-small st-btn-danger-ghost"
                onClick={() => setPlan(p => ({ ...p, days: p.days.filter((_, i) => i !== di) }))}
              >
                {t('strength.editor.removeDay')}
              </button>
            )}
          </div>

          {day.exercises.map((exercise, ei) => {
            const info = catalogByKey[exercise.exerciseKey];
            const timed = info?.kind === 'Timed';
            return (
              <div key={`${exercise.exerciseKey}-${ei}`} className="st-editor-exercise">
                <button type="button" className="st-editor-exercise-name" onClick={() => setPicker({ dayIndex: di, exerciseIndex: ei })} title={t('strength.editor.swap')}>
                  {info?.name || exercise.exerciseKey} <span aria-hidden="true">⇄</span>
                </button>
                <div className="st-editor-fields">
                  <NumberField label={t('strength.editor.sets')} value={exercise.targetSets} min={1} max={10} width="3.5rem"
                    onChange={(v) => updateExercise(di, ei, { targetSets: v ?? 1 })} />
                  {timed ? (
                    <NumberField label={t('strength.editor.seconds')} value={exercise.targetDurationSec} min={5} max={1800} step={5}
                      onChange={(v) => updateExercise(di, ei, { targetDurationSec: v })} />
                  ) : (
                    <>
                      <NumberField label={t('strength.editor.repsMin')} value={exercise.targetRepsMin} min={1} max={50} width="3.5rem"
                        onChange={(v) => updateExercise(di, ei, { targetRepsMin: v })} />
                      <NumberField label={t('strength.editor.repsMax')} value={exercise.targetRepsMax} min={1} max={50} width="3.5rem"
                        onChange={(v) => updateExercise(di, ei, { targetRepsMax: v })} />
                    </>
                  )}
                  {takesLoad(info) && (
                    <NumberField label="kg" value={exercise.targetWeightKg} step={info?.weightIncrementKg || 2.5} max={500}
                      onChange={(v) => updateExercise(di, ei, { targetWeightKg: v })} />
                  )}
                  <NumberField label={t('strength.editor.rest')} value={exercise.restSec} step={15} max={600}
                    onChange={(v) => updateExercise(di, ei, { restSec: v })} />
                </div>
                <div className="st-editor-actions">
                  <button type="button" className="st-icon-btn" disabled={ei === 0} onClick={() => moveExercise(di, ei, -1)} aria-label={t('strength.editor.moveUp')}>↑</button>
                  <button type="button" className="st-icon-btn" disabled={ei === day.exercises.length - 1} onClick={() => moveExercise(di, ei, 1)} aria-label={t('strength.editor.moveDown')}>↓</button>
                  <button type="button" className="st-icon-btn" onClick={() => removeExercise(di, ei)} aria-label={t('strength.common.delete')}>✕</button>
                </div>
                <input
                  className="st-input st-editor-notes"
                  placeholder={t('strength.editor.cue')}
                  value={exercise.notes || ''}
                  maxLength={500}
                  onChange={(e) => updateExercise(di, ei, { notes: e.target.value })}
                />
              </div>
            );
          })}

          <button type="button" className="st-btn st-btn-ghost st-btn-small" onClick={() => setPicker({ dayIndex: di, exerciseIndex: null })}>
            ＋ {t('strength.logger.addExercise')}
          </button>
        </fieldset>
      ))}

      {plan.days.length < 7 && (
        <button
          type="button"
          className="st-btn st-btn-ghost"
          onClick={() => setPlan(p => ({ ...p, days: [...p.days, { name: t('strength.editor.dayName', { n: p.days.length + 1 }), exercises: [] }] }))}
        >
          ＋ {t('strength.editor.addDay')}
        </button>
      )}

      {error && <p className="st-error" role="alert">{error}</p>}

      <div className="st-row st-row-end">
        <button type="button" className="st-btn" onClick={onCancel}>{t('strength.common.cancel')}</button>
        <button type="submit" className="st-btn st-btn-primary" disabled={saving}>{t('strength.common.save')}</button>
      </div>

      {picker && (
        <ExercisePicker
          t={t}
          lang={lang}
          catalog={catalog}
          excludeKeys={plan.days[picker.dayIndex].exercises.map(e => e.exerciseKey)}
          onPick={pick}
          onClose={() => setPicker(null)}
        />
      )}
    </form>
  );
}

export default PlanEditor;
